import type {
  CompetenceGoal, Curriculum, CurriculumPath, CurriculumVersion, Pathway, Stage, StageId,
} from '@/core/types';
import { L } from '@/i18n';

export const STAGES: Stage[] = [
  ...Array.from({ length: 10 }, (_, i) => ({
    id: `G${i + 1}` as StageId,
    label: `Grade ${i + 1}`,
    labelNo: `${i + 1}. trinn`,
    level: (i < 7 ? 'primary' : 'lower-secondary') as Stage['level'],
    order: i + 1,
  })),
  { id: 'VG1', label: 'Upper secondary 1 (Vg1)', labelNo: 'Vg1', level: 'upper-secondary', order: 11 },
  { id: 'VG2', label: 'Upper secondary 2 (Vg2)', labelNo: 'Vg2', level: 'upper-secondary', order: 12 },
  { id: 'VG3', label: 'Upper secondary 3 (Vg3)', labelNo: 'Vg3', level: 'upper-secondary', order: 13 },
];

export const STAGE_BY_ID: Record<StageId, Stage> = Object.fromEntries(STAGES.map((s) => [s.id, s])) as Record<StageId, Stage>;
export const stageOrder = (s: StageId) => STAGE_BY_ID[s].order;
export const stageLabel = (s: StageId) => L(STAGE_BY_ID[s].label, STAGE_BY_ID[s].labelNo);
export const pathwayLabel = (p: Pathway) => L(p.label, p.labelNo);
const PATHWAY_DESC_NB: Record<string, string> = {
  compulsory: 'Barneskole (1.–7.) og ungdomsskole (8.–10.).',
  studiespesialisering: 'Generell studieforberedende med programfagvalg fra Vg2.',
  realfag: 'Studieforberedende med realfag og matematikk som programområde.',
  'yrkesfag-elektro': 'Yrkesfaglig løp med læretid etterpå. Fellesfagene er tilpasset programmet.',
  'yrkesfag-helse': 'Yrkesfaglig løp med fellesfag tilpasset programmet.',
  'yrkesfag-bygg': 'Yrkesfaglig løp med fellesfag tilpasset programmet.',
  pabygg: 'Påbygging som gir generell studiekompetanse etter et yrkesfaglig løp.',
};
export const pathwayDescription = (p: Pathway) => L(p.description, PATHWAY_DESC_NB[p.id] ?? p.description);
export const stageShort = (s: StageId) => (s.startsWith('G') ? L(`G${s.slice(1)}`, `${s.slice(1)}.tr`) : L(s, `Vg${s.slice(2)}`));

export const PATHWAYS: Pathway[] = [
  {
    id: 'compulsory', label: 'Compulsory school', labelNo: 'Grunnskole', kind: 'compulsory',
    stages: ['G1', 'G2', 'G3', 'G4', 'G5', 'G6', 'G7', 'G8', 'G9', 'G10'],
    description: 'Primary (1–7) and lower secondary (8–10).',
  },
  {
    id: 'studiespesialisering', label: 'Study preparation', labelNo: 'Studiespesialisering', kind: 'study-preparatory',
    stages: ['VG1', 'VG2', 'VG3'],
    description: 'General study preparation with programme-area choices from Vg2.',
  },
  {
    id: 'realfag', label: 'Study preparation — sciences', labelNo: 'Studiespesialisering: realfag', kind: 'study-preparatory',
    stages: ['VG1', 'VG2', 'VG3'],
    description: 'Study preparation with a science/mathematics programme area.',
  },
  {
    id: 'yrkesfag-elektro', label: 'Vocational — electrical & computer tech', labelNo: 'Elektro og datateknologi', kind: 'vocational',
    stages: ['VG1', 'VG2'],
    description: 'Vocational route followed by apprenticeship. Common-core subjects are adapted to the programme.',
  },
  {
    id: 'yrkesfag-helse', label: 'Vocational — health & childhood', labelNo: 'Helse- og oppvekstfag', kind: 'vocational',
    stages: ['VG1', 'VG2'],
    description: 'Vocational route with programme-adapted common-core subjects.',
  },
  {
    id: 'yrkesfag-bygg', label: 'Vocational — building & construction', labelNo: 'Bygg- og anleggsteknikk', kind: 'vocational',
    stages: ['VG1', 'VG2'],
    description: 'Vocational route with programme-adapted common-core subjects.',
  },
  {
    id: 'pabygg', label: 'Supplementary year', labelNo: 'Påbygging til generell studiekompetanse', kind: 'study-preparatory',
    stages: ['VG3'],
    description: 'Supplementary programme giving general university admission after a vocational route.',
  },
];

export const PATHWAY_BY_ID = Object.fromEntries(PATHWAYS.map((p) => [p.id, p])) as Record<Pathway['id'], Pathway>;

export function pathwaysForStage(stage: StageId): Pathway[] {
  return PATHWAYS.filter((p) => p.stages.includes(stage));
}

export const CURRICULUM_VERSIONS: CurriculumVersion[] = [
  {
    id: 'LK20', label: 'LK20 (Kunnskapsløftet 2020)', status: 'current', effectiveFrom: '2020',
    note: 'Current national curriculum framework. Official texts must be imported from Udir.',
  },
  {
    id: 'LK06', label: 'LK06 (Kunnskapsløftet 2006)', status: 'previous', effectiveFrom: '2006',
    note: 'Previous framework — kept for version-aware history only.',
  },
];

/**
 * Official curriculum REFERENCES. Codes are references that require
 * verification against udir.no — the UI shows them as unverified. Competence
 * goal text is never authored by Studio (text: null until imported).
 */
const ref = (
  id: string, subjectId: string, code: string, title: string, stages: StageId[],
  pathways: Curriculum['pathways'], track?: string,
): Curriculum => ({
  id, subjectId, code, title, versionId: 'LK20', stages, pathways, track,
  provenance: 'OFFICIAL', verification: code === 'pending import' ? 'UNVERIFIED_REFERENCE' : 'VERIFIED',
  sourceHint: code === 'pending import'
    ? 'Not imported yet — verify at udir.no (Læreplanverket).'
    : `Imported from Udir's Grep API (data.udir.no/kl06/v201906/laereplaner-lk20/${code}), NLOD licence.`,
  competenceGoalIds: stages.map((s) => `${id}.cg.${s}`),
});

const G1_10: StageId[] = ['G1', 'G2', 'G3', 'G4', 'G5', 'G6', 'G7', 'G8', 'G9', 'G10'];

export const CURRICULA: Curriculum[] = [
  ref('cur.mat.1-10', 'math', 'MAT01-06', 'Læreplan i matematikk 1.–10. trinn', G1_10, ['compulsory']),
  ref('cur.mat.1T', 'math', 'pending import', 'Matematikk fellesfag Vg1 teoretisk (1T)', ['VG1'], ['studiespesialisering', 'realfag'], 'T'),
  ref('cur.mat.1P', 'math', 'pending import', 'Matematikk fellesfag Vg1 praktisk (1P)', ['VG1'], ['studiespesialisering', 'realfag', 'pabygg'], 'P'),
  ref('cur.mat.1PY', 'math', 'pending import', 'Matematikk fellesfag Vg1 yrkesfag (1P-Y)', ['VG1'], ['yrkesfag-elektro', 'yrkesfag-helse', 'yrkesfag-bygg'], 'PY'),
  ref('cur.mat.2P', 'math', 'pending import', 'Matematikk fellesfag Vg2 praktisk (2P)', ['VG2'], ['studiespesialisering', 'realfag', 'pabygg'], 'P'),
  ref('cur.mat.R', 'math', 'pending import', 'Matematikk for realfag (R1/R2)', ['VG2', 'VG3'], ['realfag'], 'R'),
  ref('cur.mat.S', 'math', 'pending import', 'Matematikk for samfunnsfag (S1/S2)', ['VG2', 'VG3'], ['studiespesialisering', 'realfag'], 'S'),
  ref('cur.nor', 'norwegian', 'NOR01-08', 'Læreplan i norsk', [...G1_10, 'VG1', 'VG2', 'VG3'], ['compulsory', 'studiespesialisering', 'realfag', 'pabygg', 'yrkesfag-elektro', 'yrkesfag-helse', 'yrkesfag-bygg']),
  ref('cur.eng', 'english', 'ENG01-06', 'Læreplan i engelsk', [...G1_10, 'VG1'], ['compulsory', 'studiespesialisering', 'realfag', 'yrkesfag-elektro', 'yrkesfag-helse', 'yrkesfag-bygg']),
  ref('cur.nat', 'science', 'NAT01-05', 'Læreplan i naturfag', [...G1_10, 'VG1'], ['compulsory', 'studiespesialisering', 'realfag', 'yrkesfag-elektro', 'yrkesfag-helse', 'yrkesfag-bygg']),
  ref('cur.saf', 'social', 'SAF01-05', 'Læreplan i samfunnsfag', [...G1_10, 'VG1', 'VG2'], ['compulsory', 'studiespesialisering', 'realfag', 'yrkesfag-elektro', 'yrkesfag-helse', 'yrkesfag-bygg']),
  ref('cur.rle', 'krle', 'RLE01-04', 'Læreplan i kristendom, religion, livssyn og etikk (KRLE)', G1_10, ['compulsory']),
  ref('cur.fsp.fr', 'french', 'FSP01-04', 'Læreplan i fremmedspråk — French', ['G8', 'G9', 'G10', 'VG1', 'VG2'], ['compulsory', 'studiespesialisering', 'realfag']),
  ref('cur.fsp.de', 'german', 'FSP01-04', 'Læreplan i fremmedspråk — German', ['G8', 'G9', 'G10', 'VG1', 'VG2'], ['compulsory', 'studiespesialisering', 'realfag']),
];

export const CURRICULUM_BY_ID = Object.fromEntries(CURRICULA.map((c) => [c.id, c])) as Record<string, Curriculum>;

export const COMPETENCE_GOALS: CompetenceGoal[] = CURRICULA.flatMap((c) =>
  c.stages.map((stage) => ({
    id: `${c.id}.cg.${stage}`,
    curriculumId: c.id,
    stage,
    provenance: 'OFFICIAL' as const,
    text: null,
    importStatus: 'NOT_IMPORTED' as const,
    studioSkillIds: [],
  })),
);

export const CURRICULUM_PATHS: CurriculumPath[] = [
  {
    id: 'math.compulsory', subjectId: 'math', label: 'Compulsory mathematics', description: 'Grades 1–10.',
    pathwayIds: ['compulsory'],
    steps: G1_10.map((stage) => ({ stage, track: 'core', curriculumId: 'cur.mat.1-10' })),
  },
  {
    id: 'math.theoretical-R', subjectId: 'math', label: '1T → R1 → R2', description: 'Theoretical route into science mathematics.',
    pathwayIds: ['realfag', 'studiespesialisering'],
    steps: [{ stage: 'VG1', track: 'T' }, { stage: 'VG2', track: 'R' }, { stage: 'VG3', track: 'R' }],
  },
  {
    id: 'math.theoretical-S', subjectId: 'math', label: '1T → S1 → S2', description: 'Theoretical route into social-science mathematics.',
    pathwayIds: ['realfag', 'studiespesialisering'],
    steps: [{ stage: 'VG1', track: 'T' }, { stage: 'VG2', track: 'S' }, { stage: 'VG3', track: 'S' }],
  },
  {
    id: 'math.practical', subjectId: 'math', label: '1P → 2P', description: 'Practical mathematics route.',
    pathwayIds: ['studiespesialisering', 'realfag', 'pabygg'],
    steps: [{ stage: 'VG1', track: 'P' }, { stage: 'VG2', track: 'P' }],
  },
  {
    id: 'math.vocational', subjectId: 'math', label: '1P-Y', description: 'Programme-adapted mathematics for vocational routes.',
    pathwayIds: ['yrkesfag-elektro', 'yrkesfag-helse', 'yrkesfag-bygg'],
    steps: [{ stage: 'VG1', track: 'PY' }],
  },
];

/** Maths track options available at each upper-secondary stage, per pathway kind. */
export function mathTrackOptions(stage: StageId, pathway: Pathway['id']): { id: string; label: string }[] {
  const vocational = pathway.startsWith('yrkesfag');
  if (stage === 'VG1') {
    return vocational
      ? [{ id: 'PY', label: L('1P-Y (vocational)', '1P-Y (yrkesfag)') }]
      : [{ id: 'T', label: L('1T (theoretical)', '1T (teoretisk)') }, { id: 'P', label: L('1P (practical)', '1P (praktisk)') }];
  }
  if (stage === 'VG2') {
    return vocational ? [] : [
      { id: 'R', label: L('R1 (science maths)', 'R1 (realfagsmatematikk)') },
      { id: 'S', label: L('S1 (social-science maths)', 'S1 (samfunnsfaglig matematikk)') },
      { id: 'P', label: L('2P (practical)', '2P (praktisk)') },
    ];
  }
  if (stage === 'VG3') {
    return vocational ? [] : pathway === 'pabygg'
      ? [{ id: 'P', label: L('2P (practical)', '2P (praktisk)') }]
      : [{ id: 'R', label: 'R2' }, { id: 'S', label: 'S2' }];
  }
  return [];
}
